import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DownloadIcon, FileTextIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useMyReceipts } from '@/entities/receipts';
import { formatUsd } from '@/shared/utils/currency';
import { formatDate } from '@/shared/utils/date';

const BACK_TO = '/settings';

export const PagesPaymentsMain = () => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const query = useMyReceipts();

  const receipts = query.data ?? [];

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} showBack backTo={BACK_TO} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {query.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : query.isError ? (
          <ErrorState
            message={query.error ? extractErrorMessage(query.error) : t('loadError')}
            onRetry={() => void query.refetch()}
          />
        ) : receipts.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-card">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-ink-faint">
              <FileTextIcon className="h-6 w-6" aria-hidden />
            </span>
            <div>
              <p className="text-base font-semibold text-ink">{t('emptyTitle')}</p>
              <p className="mt-1 text-sm text-ink-muted">{t('emptyDescription')}</p>
            </div>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {receipts.map((receipt) => (
              <li
                key={receipt.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {receipt.parcel_name} · {receipt.trek_id}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-ink-muted">
                      {t('number', { number: receipt.number })} · {formatDate(receipt.created_at)}
                    </p>
                  </div>
                  <span className="shrink-0 text-base font-bold text-brand-700 tabular-nums">
                    {formatUsd(Number(receipt.amount))}
                  </span>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/receipt/${receipt.id}`)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700 active:opacity-80"
                  >
                    {t('view')}
                  </button>
                  {receipt.file_url ? (
                    <a
                      href={receipt.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-ink active:opacity-70"
                    >
                      <DownloadIcon className="h-4 w-4" aria-hidden />
                      {t('pdf')}
                    </a>
                  ) : (
                    <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-400">
                      <DownloadIcon className="h-4 w-4" aria-hidden />
                      {t('pdf')}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};
